import {
  type AddTenantUserRequest,
  type GetTenantUsersResponse,
  type ChangeTenantUserNameRequest,
  type ChangeTenantUserPhoneNumberRequest,
  type ChangeTenantUserEmailRequest,
  type ScheduleUserDeactivationRequest,
  type ChangeTenantUserProfileImageRequest,
  type DeactivateUserRequest,
  type ActivateUserRequest,
  type GrantDatabaseAccessRequest,
  type RevokeDatabaseAccessRequest,
  type GrantTenantAdminPrivilegesRequest,
  type RevokeTenantAdminPrivilegesRequest,
  type GetTenantUserResponse,
  type GetDatabaseAccessGrantResponse,
  type GetDatabaseAccessGrantsResponse,
  type GrantDatabaseAccessResponse,
  type RevokeDatabaseAccessResponse,
  type GetTenantOwnedDatabasesResponse,
  type TenantUser,
  type ChangeTenantUserTitleRequest
} from '@gat-ship/types'
import { toISODateTimeString } from '@gat-ship/types'
import { useGsCloudBackend } from '@gat-ship/http-client'

export function useTenantUserApiCalls() {
  const { apiGet, apiPost, apiPut, apiDelete } = useGsCloudBackend()

  function mapTenantUser(user: TenantUser): TenantUser {
    user.activeFrom = toISODateTimeString(user.activeFrom)!
    user.activeTo = toISODateTimeString(user.activeTo)!
    return user
  }

  async function getTenantOwnedDatabasesRequest() {
    const response = await apiGet<GetTenantOwnedDatabasesResponse>(
      `/access-control/databases`
    )
    return response
  }

  async function getTenantUsersRequest() {
    const response = await apiGet<GetTenantUsersResponse>(
      '/access-control/users'
    )
    response.users = response.users.map(mapTenantUser)
    return response
  }

  async function getTenantUserRequest(userId: string) {
    const response = await apiGet<GetTenantUserResponse>(
      `/access-control/users/${userId}`
    )
    response.user = mapTenantUser(response.user)
    return response
  }

  function addTenantUserRequest(
    request: AddTenantUserRequest,
    commandId: string
  ) {
    return apiPost<undefined>('/access-control/users', request, commandId)
  }

  function changeNameOfUserRequest(
    request: ChangeTenantUserNameRequest,
    commandId: string
  ) {
    return apiPut<undefined, ChangeTenantUserNameRequest>(
      `/access-control/users/${request.userId}/change-name`,
      request,
      commandId
    )
  }

  function changeTenantUserTitleRequest(
    request: ChangeTenantUserTitleRequest,
    commandId: string
  ) {
    return apiPut<undefined, ChangeTenantUserTitleRequest>(
      `/access-control/users/${request.userId}/change-title`,
      request,
      commandId
    )
  }

  function changeTenantUserEmailRequest(
    request: ChangeTenantUserEmailRequest,
    commandId: string
  ) {
    return apiPut<undefined, ChangeTenantUserEmailRequest>(
      `/access-control/users/${request.userId}/change-email`,
      request,
      commandId
    )
  }

  /** Schedules deactivation of a tenant user at a given time in the future. */
  function scheduleUserDeactivationRequest(
    request: ScheduleUserDeactivationRequest,
    commandId: string
  ) {
    return apiPut<undefined, ScheduleUserDeactivationRequest>(
      `/access-control/users/${request.userId}/schedule-deactivation`,
      request,
      commandId
    )
  }

  function changeTenantUserPhoneNumberRequest(
    request: ChangeTenantUserPhoneNumberRequest,
    commandId: string
  ) {
    return apiPut<undefined, ChangeTenantUserPhoneNumberRequest>(
      `/access-control/users/${request.userId}/change-mobile-phone-number`,
      request,
      commandId
    )
  }

  /** Replaces the current profile image for a tenant user. */
  function changeTenantUserProfileImageRequest(
    request: ChangeTenantUserProfileImageRequest,
    commandId: string
  ) {
    return apiPut<undefined, ChangeTenantUserProfileImageRequest>(
      `/access-control/users/${request.userId}/change-profile-image`,
      request,
      commandId
    )
  }

  function grantTenantAdminPrivilegesRequest(
    request: GrantTenantAdminPrivilegesRequest,
    commandId: string
  ) {
    return apiPut<undefined, GrantTenantAdminPrivilegesRequest>(
      `/access-control/users/${request.userId}/grant-tenant-admin`,
      request,
      commandId
    )
  }

  function revokeTenantAdminPrivilegesRequest(
    request: RevokeTenantAdminPrivilegesRequest,
    commandId: string
  ) {
    return apiPut<undefined, RevokeTenantAdminPrivilegesRequest>(
      `/access-control/users/${request.userId}/revoke-tenant-admin`,
      request,
      commandId
    )
  }

  /**
   * Deactivates an tenant user immediately.
   * @param request - The request object containing the user ID.
   * @param commandId - The command ID.
   * @returns The response object containing the user ID.
   */
  function deactivateUserRequest(
    request: DeactivateUserRequest,
    commandId: string
  ) {
    return apiPut<undefined>(
      `/access-control/users/${request.userId}/deactivate`,
      undefined,
      commandId
    )
  }

  /** Activates a tenant user immediately. */
  function activateUserRequest(
    request: ActivateUserRequest,
    commandId: string
  ) {
    return apiPut<undefined>(
      `/access-control/users/${request.userId}/activate`,
      {
        activeFrom: request.activeFrom
      },
      commandId
    )
  }

  /** Checks if a given email address is available for use. */
  function checkTenantUserEmailAvailabilityRequest(email: string) {
    return apiGet<boolean>(
      `/access-control/users/check-email-available?email=${email}`
    )
  }

  /** Get database access grants for a tenant user.
   * @param userId - The ID of the tenant user.
   * @returns The database access grants for the tenant user.
   */
  function getDatabaseAccessGrantsRequest(userId: string) {
    return apiGet<GetDatabaseAccessGrantsResponse>(
      `/access-control/users/${userId}/database-grants`
    )
  }

  /** Get a single database grant for a tenant user.
   * @param userId - The ID of the tenant user.
   * @param grantId - The ID of the grant in UUID format.
   * @returns The database grant for the tenant user.
   */
  function getDatabaseAccessGrantRequest(userId: string, grantId: string) {
    return apiGet<GetDatabaseAccessGrantResponse>(
      `/access-control/users/${userId}/database-grants/${grantId}`
    )
  }

  function grantDatabaseAccessRequest(
    request: GrantDatabaseAccessRequest,
    commandId: string
  ) {
    return apiPost<GrantDatabaseAccessResponse>(
      `/access-control/users/${request.userId}/database-grants`,
      request,
      commandId
    )
  }

  function revokeDatabaseAccessRequest(
    request: RevokeDatabaseAccessRequest,
    commandId: string
  ) {
    return apiDelete<RevokeDatabaseAccessResponse>(
      `/access-control/users/${request.userId}/database-grants/${request.grantId}`,
      undefined,
      commandId
    )
  }

  return {
    getTenantOwnedDatabasesRequest,
    getTenantUsersRequest,
    getTenantUserRequest,
    addTenantUserRequest,
    changeNameOfUserRequest,
    changeTenantUserTitleRequest,
    changeTenantUserEmailRequest,
    scheduleUserDeactivationRequest,
    changeTenantUserPhoneNumberRequest,
    changeTenantUserProfileImageRequest,
    grantTenantAdminPrivilegesRequest,
    revokeTenantAdminPrivilegesRequest,
    deactivateUserRequest,
    activateUserRequest,
    checkTenantUserEmailAvailabilityRequest,
    getDatabaseAccessGrantsRequest,
    getDatabaseAccessGrantRequest,
    grantDatabaseAccessRequest,
    revokeDatabaseAccessRequest
  }
}
